import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useLoggedInUser } from '../useLoggedInUser/useLoggedInUser'
import { useAlert } from '~/@ui/alert/useAlert'
import { phoneScheme, PhoneScheme } from '../phone-login/_scheme/phone.scheme'
import { usePhoneRegisterVerification } from './usePhoneRegisterVerification'
import { PhoneInput } from '~/@ui/phone/PhoneInput'
import { CodeInput } from '~/@ui/phone/CodeInput'

export const PhoneRegisterInvestorPage = () => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { control, getValues, trigger } = useForm<PhoneScheme>({
    resolver: zodResolver(phoneScheme),
    mode: 'onBlur',
    defaultValues: {
      phonePrefix: '+421',
      phoneNumber: '',
    },
  })

  const {
    isVerifyingPhone,
    startVerification,
    stopVerification,

    verificationCode,
    verifyCodeInputState,

    handleCodeChange,
    handleSendAgainClick,

    waiting,
    secondsLeft,

    isStartLoading,
    isVerifyLoading,
  } = usePhoneRegisterVerification({
    onSuccess: () => {
      navigate('/investor/portfolio')
    },
  })

  const state = waiting || isStartLoading || isVerifyLoading
  const verifyCodeInputClassName = `i-verifycodeinput-${verifyCodeInputState}`

  const handleSubmitClick = async () => {
    const isValid = await trigger()

    if (!isValid) {
      showAlert('error', 'Prosím, vyplňte tel. č. správne.')
      return
    }

    const phone = `${getValues('phonePrefix')} ${getValues('phoneNumber')}`

    startVerification(phone)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSubmitClick()
    }
  }

  const { hasAuthToken, checkLoggedInUser } = useLoggedInUser()

  useEffect(() => {
    const loadUserOrNavigateToLogin = async () => {
      if (hasAuthToken()) {
        const userResponse = await checkLoggedInUser()

        if (userResponse && 'success' in userResponse && userResponse.success) {
          navigate('/investor/portfolio')
        }
      }
    }

    loadUserOrNavigateToLogin()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return !isStartLoading && isVerifyingPhone ? (
    <CodeInput
      title="Skontrolujte si telefón"
      stopVerification={stopVerification}
      getValues={getValues}
      verificationCode={verificationCode}
      handleCodeChange={handleCodeChange}
      verifyCodeInputClassName={verifyCodeInputClassName}
      state={state}
      secondsLeft={secondsLeft}
      handleSendAgainClick={handleSendAgainClick}
      showGdrp
    />
  ) : (
    <PhoneInput
      title="Registrovať sa ako investor"
      text="Vytvoriť účet je jednoduché. Najprv zadajte telefónne číslo."
      onClickBack={() => navigate('/account/register')}
      continueButtonLabel="Overiť číslo"
      questionText="Máte u nás účet?"
      linkLabel="Prihlásiť sa"
      link="/investor/login"
      control={control}
      handleKeyDown={handleKeyDown}
      handleSubmitClick={handleSubmitClick}
      isStartLoading={isStartLoading}
    />
  )
}
